import React, { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { EonUiPreloaderSquares } from "@eon-ui/eon-ui-components-react";
import { AiAssistContext, useAiAssistContext } from "../AiAssist.context";
import { lookupTableAboutChatbotActions } from "../action-components/actions";
import userQueryComponent from "../action-components/userQuery.component";
import { getFrom } from "../../../util/map";
import AiWelcomeWrapper from "./AiWelcome";
import { Feedback } from "../../feedback/feedback.component";
import { AiAssistFeedbackPayload, LikeOrDislike, extractSourceFromAnswer } from "@me8eon/feedback";
import LiveAgentChat from "../../live-agent-chat/LiveAgentChat";
import useSearchStore from "../../../store/searchStore";
import QuickActionLinks from "../QuickActionLinks";
import { useAiSourceMemory } from "../../ai/clients/ai.rag.client";
import { ESApiFinalResponseInterface } from "../../../interfaces/ElasticSearchInterface";
import AssistanceTask from "../assistance-task/AssistanceTask";
import { indexToNameMap } from "../hooks/useAiData";
import { MessageWithActionAndData } from "@me8eon/sessions_messages";
import { RequestCallbackProvider } from "../../request-callback/RequestCallbackContext";
import { useUserData } from "@me8eon/react_authentication";
import { CopyShareButtons } from "../../feedback/CopyShareButtons.component";
import { useFeatureFlag } from "@me8eon/featureflags";
import UserMessage from "../../messages/user/UserMessage";
import AssistantMessage from "../../messages/assistant/AssistantMessage";
import ManualTask from "../assistance-task/ManualTask";
import { useAiWithRag } from "../../ai/contexts/ai.rag.context";
import { categoryPrompt } from "./utils/category.prompt";
import { Message } from "../../ai/ai.types";

export function getFeedbackDataFromSource(s: ESApiFinalResponseInterface) {
    const machedData = Object.keys(indexToNameMap).filter((indexObj) => s.index.match(indexObj));
    const summary = indexToNameMap[machedData[0]];
    return ({ summary, index: s.index, id: s.id });
}

const Content: React.FC = () => {
    const { showLiveAgentChat } = useSearchStore();
    const [sources] = useAiSourceMemory();
    const { messages, actualMessages, isLoading, cards, currentCardKey, isITIssue, quickActionType, isChatHistory } =
        useContext(AiAssistContext);
    const messageEndRef = useRef<HTMLDivElement>(null);
    const userData = useUserData();
    const { baseAiClient } = useAiWithRag()

    const showFeedback = useMemo(
        () => actualMessages[actualMessages.length - 1]?.role === "assistant" && messages[messages.length - 1]?.action !== "quick-actions" && !isLoading,
        [actualMessages, isLoading],
    );
    const showCreateIncident = useMemo(
        () => cards[currentCardKey]?.feedback?.includes("create-incident") && (quickActionType || isITIssue),
        [cards, currentCardKey],
    );

    // Cache classification result to avoid multiple API calls
    const classificationCache = useRef<{ messagesLength: number; result: any } | null>(null);

    const feedbackDataHandler = useCallback(
        async (like: LikeOrDislike | undefined): Promise<AiAssistFeedbackPayload> => {
            // Only fetch classification once per conversation state
            let classification;
            if (classificationCache.current?.messagesLength === actualMessages.length) {
                classification = classificationCache.current.result;
            } else {
                classification = await baseAiClient.sendRequest([
                    ...messages.filter(m => m.role === "user" || m.role === "assistant" || m.role === "system").map(({ role, content }) => ({ role, content })),
                    { role: "system" as const, content: categoryPrompt }
                ], {
                    response_format: {
                        type: "json_object"
                    }
                }).then(res => JSON.parse(res[0].content)).catch(err => {
                    console.error(err)
                    return {
                        classification: "categorizing_failed",
                        confidence: 1,
                        reason: `Error occured during categorization: ${err.message}`
                    }
                });

                classificationCache.current = {
                    messagesLength: actualMessages.length,
                    result: classification
                };
            }

            const answer = actualMessages[actualMessages.length - 1]?.content || "";
            const sourceFromAnswer = extractSourceFromAnswer(answer as string);

            return {
                like,
                sources: sources.map(getFeedbackDataFromSource),
                classification,
                source: "ai-assist",
                messages: actualMessages,
                query: actualMessages[actualMessages.length - 2]?.content as string || "",
                answer: answer as string,
                sourceFromAnswer: sourceFromAnswer,
                company: userData.company || "unknown",
                role: userData.currentRole || "unknown"
            };
        },
        [actualMessages, baseAiClient, messages, sources, userData.company],
    );

    const messageComponents = useMemo(
        () => {
            return messages.filter((m) => m.role === "assistant" || m.role === "user").map((message, index: number) => {
                switch (message.role) {
                case "user": return <UserMessage {...message} key={`user-${index}`} />;
                case "assistant": return <AssistantMessage {...message} key={`assistant-${index}`} />;
                }
            })
        },
        [messages],
    );

    useEffect(() => {
        if (messageEndRef.current) {
            if (!isChatHistory) messageEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    return (
        <>
            {messages.length <= 0 ? (
                <AiWelcomeWrapper eonInternetCardKey={currentCardKey} />
            ) : (
                <div className="ai-assist-content-wrapper">
                    <div className="ai-assist-content">
                        {messageComponents}
                        {isLoading && <div className="ai-assist-preloader-wrapper">
                            <EonUiPreloaderSquares
                                className="preloader"
                            />
                        </div>}
                        {showFeedback && (<CopyShareButtons
                            contentToCopy={actualMessages[actualMessages.length - 1]?.content || ""}
                            messagesToShare={actualMessages}
                        />)}
                        {showFeedback && (
                            <Feedback
                                dataToSend={feedbackDataHandler}
                                showCreateIncident={!!showCreateIncident}
                                source="ai-assistance"
                            />
                        )}
                        <div ref={messageEndRef} />
                    </div>
                </div>
            )}
            {showLiveAgentChat && <LiveAgentChat />}
        </>
    );
};

const AiMessages: React.FC = () => {
    const { messages, currentCardKey } = useContext(AiAssistContext);

    return (
        <>
            <div className="ai-assist-wrapper">
                <div className="ai-assit-container" style={{ alignContent: messages.length ? "normal" : "center" }}>
                    <RequestCallbackProvider>
                        <Content />
                    </RequestCallbackProvider>
                </div>
                <div className="ai-assist-right-panel-desktop">
                    {currentCardKey === "selfService" && <QuickActionLinks />}
                    <AssistanceTask />
                    <ManualTask />
                </div>
            </div>
        </>
    );
};

export default AiMessages;
